import React, { useState, useMemo } from "react";
import Fuse from "fuse.js";
import type { JSX } from "react";
import {
  Button,
  Input,
  NavbarContainer,
  SpaceFilters,
  SpaceFiltersSkeleton,
  SpaceCard,
  SpaceCardSkeleton,
  Skeleton,
  SkeletonStatus,
  PublicPageGuideModal,
} from "@/components";
import { useSpaces, usePublicPageGuide } from "@/hooks";
import {
  SPACES_PAGE_EMPTY,
  SPACES_PAGE_GUIDE_ACTION_TRACK_SUFFIX,
  SPACES_PAGE_GUIDE_CONFIRM_LABEL,
  SPACES_PAGE_GUIDE_INTRO,
  SPACES_PAGE_GUIDE_STEPS,
  SPACES_PAGE_GUIDE_STORAGE_KEY,
  SPACES_PAGE_GUIDE_TITLE,
  SPACES_PAGE_LEDE,
} from "@/data/public/spacesPage";
import type { Zone } from "@/types";
import { ZONE_LABELS } from "@/constants";
import "./Spaces.scss";

/**
 * Page directory of theater spaces/rooms in the city
 * Allows filtering by zone and name search, as well as sorting
 * @returns {JSX.Element} The spaces page component
 */
const Spaces: React.FC = (): JSX.Element => {
  const { data: spaces, loading, error } = useSpaces();
  const { isGuideOpen, closeGuideForSession, dismissGuidePermanently } =
    usePublicPageGuide(SPACES_PAGE_GUIDE_STORAGE_KEY, !loading);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [sortOrder, setSortOrder] = useState<"name-asc" | "name-desc">(
    "name-asc",
  );
  const availableZones = useMemo(
    () => Array.from(new Set(spaces.flatMap((loc) => loc.zones))),
    [spaces],
  );
  const hasActiveFilters = Boolean(searchTerm.trim() || selectedZone);
  const filteredSpaces = useMemo(() => {
    let result = [...spaces];
    if (searchTerm) {
      const fuse = new Fuse(spaces, {
        keys: ["name", "address"],
        threshold: 0.3,
      });
      result = fuse.search(searchTerm).map((res) => res.item);
      setSelectedZone(null);
    }
    if (selectedZone) {
      result = result.filter((space) => space.zones.includes(selectedZone));
      setSearchTerm("");
    }
    result.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortOrder === "name-asc" ? comparison : -comparison;
    });
    return result;
  }, [spaces, searchTerm, selectedZone, sortOrder]);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  const handleZoneChange = (zone: Zone | null) => {
    setSelectedZone(zone === selectedZone ? null : zone);
  };
  const handleSortChange = (order: "name-asc" | "name-desc") => {
    setSortOrder(order);
  };
  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedZone(null);
  };
  return (
    <NavbarContainer title="Teatros en Córdoba Capital">
      <div className="spaces">
        {error && <p>Error al cargar salas: {error.message}</p>}
        <div className="spaces__container">
          <p className="spaces__lede">{SPACES_PAGE_LEDE}</p>
          <div className="spaces__search-container">
            {loading ? (
              <Skeleton borderRadius="base" borderWeight="base" height={57} />
            ) : (
              <Input
                type="text"
                placeholder="Buscar sala por nombre o dirección..."
                value={searchTerm}
                onChange={handleSearchChange}
                density="spacious"
                borderRadius="base"
              />
            )}
          </div>
          {loading ? (
            <>
              <SpaceFiltersSkeleton />
              <SkeletonStatus
                label="Cargando salas..."
                className="spaces__grid"
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <SpaceCardSkeleton key={`space-skeleton-${index}`} />
                ))}
              </SkeletonStatus>
            </>
          ) : (
            <>
              <SpaceFilters
                zones={availableZones}
                zoneLabels={ZONE_LABELS}
                selectedZone={selectedZone}
                sortOrder={sortOrder}
                onZoneChange={(zone) => {
                  handleZoneChange(zone as Zone);
                }}
                onSortChange={handleSortChange}
              />
              {filteredSpaces.length > 0 ? (
                <div className="spaces__grid">
                  {filteredSpaces.map((space) => (
                    <SpaceCard key={space.name} space={space} />
                  ))}
                </div>
              ) : (
                <div className="spaces__empty">
                  <p className="spaces__empty-message">
                    {spaces.length === 0
                      ? SPACES_PAGE_EMPTY.noData
                      : SPACES_PAGE_EMPTY.noMatch}
                  </p>
                  {spaces.length > 0 && hasActiveFilters ? (
                    <>
                      <p className="spaces__empty-hint">
                        {SPACES_PAGE_EMPTY.hint}
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        appearance="linear"
                        density="compact"
                        motionScale="sm"
                        actionTrackSufix="spaces_clear_filters"
                        onClick={handleClearFilters}
                      >
                        {SPACES_PAGE_EMPTY.clearAction}
                      </Button>
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <PublicPageGuideModal
        isOpen={isGuideOpen}
        onClose={closeGuideForSession}
        onConfirm={dismissGuidePermanently}
        title={SPACES_PAGE_GUIDE_TITLE}
        intro={SPACES_PAGE_GUIDE_INTRO}
        steps={SPACES_PAGE_GUIDE_STEPS}
        confirmLabel={SPACES_PAGE_GUIDE_CONFIRM_LABEL}
        actionTrackSufix={SPACES_PAGE_GUIDE_ACTION_TRACK_SUFFIX}
      />
    </NavbarContainer>
  );
};
export default Spaces;
